'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useModalFocusManagement } from '@/hooks/useModalFocusManagement';
import {
	TIMEZONE_OPTIONS,
	getTimezoneLabel,
	getTimezonesByRegion,
	formatTimezoneOffset,
} from '@/lib/timezone';

// ============================================================================
// Strings (extracted for future i18n — see docs/I18N_CONVENTIONS.md)
// ============================================================================
const STRINGS = {
	step1: {
		title: "Welcome to VastiPost",
		subtitle: "Let's set up your account. You can always change these later.",
		nameLabel: "Display Name",
		namePlaceholder: "Your name",
		timezoneLabel: "Timezone",
	},
	step2: {
		title: "Create Your First Brand",
		subtitle: "A brand is the identity you'll post and manage content under.",
		brandNameLabel: "Brand Name",
		brandNamePlaceholder: "e.g. My Brand",
		industryLabel: "Industry",
		industryPlaceholder: "Select an industry (optional)",
	},
	buttons: {
		next: "Next",
		back: "Back",
		getStarted: "Get Started",
		cancel: "Cancel",
	},
	errors: {
		nameRequired: "Please enter a display name",
		nameTooShort: "Name must be at least 2 characters",
		brandNameRequired: "Please enter a brand name",
		brandNameTooShort: "Brand name must be at least 2 characters",
		profileUpdateFailed: "Failed to save profile. Please try again.",
		brandCreateFailed: "Failed to create brand. Please try again.",
		genericError: "Something went wrong. Please try again.",
	},
	stepIndicator: (current: number, total: number) => `Step ${current} of ${total}`,
} as const;

// ============================================================================
// Industry options
// ============================================================================
const INDUSTRY_OPTIONS = [
	"Technology",
	"Fashion & Apparel",
	"Food & Beverage",
	"Health & Fitness",
	"Education",
	"Entertainment",
	"E-commerce",
	"Travel & Tourism",
	"Finance",
	"Real Estate",
	"Beauty & Cosmetics",
	"Sports",
	"Music",
	"Art & Design",
	"Non-profit",
	"Other",
] as const;

// ============================================================================
// TimezoneFormPicker — normal-flow timezone picker for forms (not Framer)
// ============================================================================
function TimezoneFormPicker({ value, onChange }: { value: string; onChange: (tz: string) => void }) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [highlightIdx, setHighlightIdx] = useState(-1);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const displayLabel = useMemo(() => (value ? getTimezoneLabel(value) : ''), [value]);

	const { items, regionHeaders } = useMemo(() => {
		const grouped = getTimezonesByRegion();
		const regionOrder = ['Americas', 'Europe', 'Asia & Pacific', 'Africa & Middle East', 'Universal'];
		const items: (typeof TIMEZONE_OPTIONS)[number][] = [];
		const regionHeaders: Record<number, string> = {};
		for (const region of regionOrder) {
			const options = (grouped[region] || []).filter((opt) => {
				if (!query.trim()) return true;
				const q = query.toLowerCase();
				return opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q) || opt.region.toLowerCase().includes(q);
			});
			if (options.length === 0) continue;
			regionHeaders[items.length] = region;
			items.push(...options);
		}
		return { items, regionHeaders };
	}, [query]);

	// Close on outside click
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	// Scroll highlighted into view
	useEffect(() => {
		if (highlightIdx < 0 || !listRef.current) return;
		const el = listRef.current.querySelector(`[data-tzidx="${highlightIdx}"]`);
		if (el) el.scrollIntoView({ block: 'nearest' });
	}, [highlightIdx]);

	const handleSelect = useCallback((opt: (typeof TIMEZONE_OPTIONS)[number]) => {
		onChange(opt.value);
		setOpen(false);
		setQuery('');
	}, [onChange]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (!open) {
			if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); }
			return;
		}
		if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, items.length - 1)); }
		else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
		else if (e.key === 'Enter') { e.preventDefault(); if (highlightIdx >= 0 && highlightIdx < items.length) handleSelect(items[highlightIdx]); }
		else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
	}, [open, items, highlightIdx, handleSelect]);

	return (
		<div ref={wrapperRef} className="relative">
			{/* Trigger / search input */}
			{open ? (
				<input
					ref={inputRef}
					type="text"
					autoComplete="off"
					value={query}
					placeholder={displayLabel || 'Search timezones...'}
					onChange={(e) => { setQuery(e.target.value); setHighlightIdx(-1); }}
					onKeyDown={handleKeyDown}
					className="w-full px-3 py-2.5 border border-[#5BA9E1] rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5BA9E1]/40 transition-colors"
					autoFocus
				/>
			) : (
				<button
					type="button"
					onClick={() => { setOpen(true); setQuery(''); setHighlightIdx(-1); requestAnimationFrame(() => inputRef.current?.focus()); }}
					className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 text-left focus:outline-none focus:ring-2 focus:ring-[#5BA9E1]/40 focus:border-[#5BA9E1] transition-colors flex items-center justify-between"
				>
					<span className={displayLabel ? 'text-gray-900' : 'text-gray-400'}>
						{displayLabel || 'Select timezone...'}
					</span>
					<svg width="12" height="12" viewBox="0 0 12 12" className="opacity-50 flex-shrink-0"><path fill="#9CA3AF" d="M2.5 4.5L6 8l3.5-3.5" /></svg>
				</button>
			)}

			{/* Dropdown */}
			{open && items.length > 0 && (
				<div
					ref={listRef}
					className="absolute left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-[10001] max-h-[240px] overflow-y-auto"
				>
					{items.map((opt, idx) => {
						const showHeader = regionHeaders[idx] !== undefined;
						return (
							<React.Fragment key={opt.value}>
								{showHeader && (
									<div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide" style={idx > 0 ? { borderTop: '1px solid #f0f0f0' } : undefined}>
										{regionHeaders[idx]}
									</div>
								)}
								<button
									type="button"
									data-tzidx={idx}
									onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
									onMouseEnter={() => setHighlightIdx(idx)}
									className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors ${idx === highlightIdx ? 'bg-blue-50' : opt.value === value ? 'bg-gray-50' : 'bg-transparent hover:bg-gray-50'}`}
								>
									<span className={opt.value === value ? 'font-medium text-gray-900' : 'text-gray-700'}>{opt.label}</span>
									<span className="text-xs text-gray-400 ml-2 whitespace-nowrap">{formatTimezoneOffset(opt.value)}</span>
								</button>
							</React.Fragment>
						);
					})}
				</div>
			)}

			{/* No results */}
			{open && query.trim() && items.length === 0 && (
				<div className="absolute left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-[10001] px-3 py-4 text-center text-sm text-gray-400">
					No matching timezones
				</div>
			)}
		</div>
	);
}

// ============================================================================
// Types
// ============================================================================
export interface OnboardingData {
	displayName: string;
	timezone: string;
	brandName: string;
	industry: string;
}

interface OnboardingModalProps {
	isOpen: boolean;
	/** Pre-filled display name (email prefix) */
	initialName: string;
	/** User's email (for detecting default name) */
	initialEmail: string;
	/** User's current timezone or 'UTC' */
	initialTimezone: string;
	/** Which step to start on (1 = name/tz, 2 = brand) */
	initialStep?: 1 | 2;
	/**
	 * 'onboarding' (default) — authenticated user; calls profile + brand APIs internally.
	 * 'signup' — pre-auth; collects data and returns via onComplete for the parent to handle.
	 */
	mode?: 'onboarding' | 'signup';
	/**
	 * Called when the user completes the flow.
	 * In 'onboarding' mode: called with no args after API calls succeed.
	 * In 'signup' mode: called with collected data; may throw to show error in modal.
	 */
	onComplete: (data?: OnboardingData) => void | Promise<void>;
	/** Called when user cancels (only used in 'signup' mode) */
	onCancel?: () => void;
}

// ============================================================================
// Component
// ============================================================================
export default function OnboardingModal({
	isOpen,
	initialName,
	initialEmail,
	initialTimezone,
	initialStep = 1,
	mode = 'onboarding',
	onComplete,
	onCancel,
}: OnboardingModalProps) {
	const [step, setStep] = useState<1 | 2>(initialStep);
	const [displayName, setDisplayName] = useState(initialName);
	const [timezone, setTimezone] = useState(initialTimezone || 'UTC');
	const [brandName, setBrandName] = useState('');
	const [industry, setIndustry] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const nameInputRef = useRef<HTMLInputElement>(null);
	const brandInputRef = useRef<HTMLInputElement>(null);
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const titleId = React.useId();
	const descriptionId = React.useId();

	useModalFocusManagement({
		dialogRef,
		enabled: isOpen,
	});

	// Reset state when modal opens
	useEffect(() => {
		if (isOpen) {
			setStep(initialStep);
			setDisplayName(initialName);
			setTimezone(initialTimezone || 'UTC');
			setBrandName('');
			setIndustry('');
			setError(null);
			setIsLoading(false);
		}
	}, [isOpen, initialName, initialTimezone, initialStep]);

	// Auto-focus inputs
	useEffect(() => {
		if (!isOpen) return;
		const timer = setTimeout(() => {
			if (step === 1) nameInputRef.current?.focus();
			if (step === 2) brandInputRef.current?.focus();
		}, 100);
		return () => clearTimeout(timer);
	}, [isOpen, step]);

	// Body scroll lock
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	// ── Step 1 → Step 2 ──────────────────────────────────────────────
	const handleNext = useCallback(() => {
		const trimmed = displayName.trim();
		if (!trimmed) {
			setError(STRINGS.errors.nameRequired);
			return;
		}
		if (trimmed.length < 2) {
			setError(STRINGS.errors.nameTooShort);
			return;
		}
		setError(null);
		setStep(2);
	}, [displayName]);

	// ── Step 2 → Back to Step 1 ──────────────────────────────────────
	const handleBack = useCallback(() => {
		setError(null);
		setStep(1);
	}, []);

	// ── Final submit: Save profile + Create brand ────────────────────
	const handleSubmit = useCallback(async () => {
		const trimmedBrand = brandName.trim();
		if (!trimmedBrand) {
			setError(STRINGS.errors.brandNameRequired);
			return;
		}
		if (trimmedBrand.length < 2) {
			setError(STRINGS.errors.brandNameTooShort);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			if (mode === 'signup') {
				// ── Signup mode: return collected data for parent to handle ──
				await onComplete({
					displayName: displayName.trim(),
					timezone,
					brandName: trimmedBrand,
					industry: industry || '',
				});
				// If onComplete doesn't throw, parent will close modal via isOpen
			} else {
				// ── Onboarding mode: call APIs directly (authenticated user) ──
				// 1. Update user profile (display name + timezone)
				const profileRes = await fetch('/api/user/profile', {
					method: 'PUT',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						fullName: displayName.trim(),
						timezone,
					}),
				});

				if (!profileRes.ok) {
					const data = await profileRes.json().catch(() => ({}));
					throw new Error(data.error || STRINGS.errors.profileUpdateFailed);
				}

				// 2. Create brand
				const brandRes = await fetch('/api/user/brands/create', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						name: trimmedBrand,
						industry: industry || undefined,
					}),
				});

				if (!brandRes.ok) {
					const data = await brandRes.json().catch(() => ({}));
					throw new Error(data.error || STRINGS.errors.brandCreateFailed);
				}

				// Success — let parent handle refresh
				onComplete();
			}
		} catch (err) {
			console.error('[OnboardingModal] Submit error:', err);
			setError(err instanceof Error ? err.message : STRINGS.errors.genericError);
		} finally {
			setIsLoading(false);
		}
	}, [displayName, timezone, brandName, industry, mode, onComplete]);

	// ── Keyboard: Enter to proceed ───────────────────────────────────
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !isLoading) {
				e.preventDefault();
				if (step === 1) handleNext();
				else handleSubmit();
			}
		},
		[step, isLoading, handleNext, handleSubmit],
	);

	// ── Guard ────────────────────────────────────────────────────────
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
			<div
				aria-describedby={descriptionId}
				aria-labelledby={titleId}
				aria-modal="true"
				className="bg-white rounded-2xl shadow-2xl p-6 w-[362px] max-w-[90vw] max-h-[85vh] overflow-y-auto"
				data-testid="onboarding-dialog"
				ref={dialogRef}
				role="dialog"
				style={{ boxShadow: '0px 10px 20px 0px rgba(0, 0, 0, 0.1)' }}
				tabIndex={-1}
				onKeyDown={handleKeyDown}
			>
				{/* Step indicator */}
				<div className="flex items-center gap-2 mb-4">
					<div className={`h-1 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-[#5BA9E1]' : 'bg-gray-200'}`} />
					<div className={`h-1 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-[#5BA9E1]' : 'bg-gray-200'}`} />
				</div>
				<p className="text-xs text-gray-400 mb-3">{STRINGS.stepIndicator(step, 2)}</p>

				{/* Error banner */}
				{error && (
					<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" role="alert">
						{error}
					</div>
				)}

				{/* ============ STEP 1: Welcome ============ */}
				{step === 1 && (
					<div>
						<h2 className="text-lg font-semibold text-gray-900 mb-1" id={titleId}>{STRINGS.step1.title}</h2>
						<p className="text-sm text-gray-500 mb-5" id={descriptionId}>{STRINGS.step1.subtitle}</p>

						{/* Display Name */}
						<label className="block text-sm font-medium text-gray-700 mb-1.5">
							{STRINGS.step1.nameLabel}
						</label>
						<input
							ref={nameInputRef}
							type="text"
							value={displayName}
							onChange={(e) => {
								setDisplayName(e.target.value);
								setError(null);
							}}
							placeholder={STRINGS.step1.namePlaceholder}
							maxLength={255}
							className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5BA9E1]/40 focus:border-[#5BA9E1] transition-colors"
						/>

						{/* Timezone */}
						<label className="block text-sm font-medium text-gray-700 mt-4 mb-1.5">
							{STRINGS.step1.timezoneLabel}
						</label>
						<TimezoneFormPicker
							value={timezone}
							onChange={(val) => {
								setTimezone(val);
								setError(null);
							}}
						/>

						{/* Next button */}
						<button
							onClick={handleNext}
							className="mt-6 w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90 active:opacity-80"
							style={{
								background: 'linear-gradient(45deg, rgb(91, 169, 225), rgb(135, 130, 230))',
							}}
							type="button"
						>
							{STRINGS.buttons.next}
						</button>

						{/* Cancel button (signup mode only) */}
						{mode === 'signup' && onCancel && (
							<button
								onClick={onCancel}
								className="mt-2 w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors"
								type="button"
							>
								{STRINGS.buttons.cancel}
							</button>
						)}
					</div>
				)}

				{/* ============ STEP 2: Create Brand ============ */}
				{step === 2 && (
					<div>
						<h2 className="text-lg font-semibold text-gray-900 mb-1" id={titleId}>{STRINGS.step2.title}</h2>
						<p className="text-sm text-gray-500 mb-5" id={descriptionId}>{STRINGS.step2.subtitle}</p>

						{/* Brand Name */}
						<label className="block text-sm font-medium text-gray-700 mb-1.5">
							{STRINGS.step2.brandNameLabel}
						</label>
						<input
							ref={brandInputRef}
							type="text"
							value={brandName}
							onChange={(e) => {
								setBrandName(e.target.value);
								setError(null);
							}}
							placeholder={STRINGS.step2.brandNamePlaceholder}
							maxLength={255}
							className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5BA9E1]/40 focus:border-[#5BA9E1] transition-colors"
						/>

						{/* Industry */}
						<label className="block text-sm font-medium text-gray-700 mt-4 mb-1.5">
							{STRINGS.step2.industryLabel}
						</label>
						<select
							value={industry}
							onChange={(e) => {
								setIndustry(e.target.value);
								setError(null);
							}}
							className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#5BA9E1]/40 focus:border-[#5BA9E1] transition-colors appearance-none"
							style={{
								backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239CA3AF' d='M2.5 4.5L6 8l3.5-3.5'/%3E%3C/svg%3E")`,
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'right 12px center',
								backgroundSize: '12px',
							}}
						>
							<option value="">{STRINGS.step2.industryPlaceholder}</option>
							{INDUSTRY_OPTIONS.map((opt) => (
								<option key={opt} value={opt}>{opt}</option>
							))}
						</select>

						{/* Buttons */}
						<div className="mt-6 flex flex-col gap-2">
							<button
								onClick={handleSubmit}
								disabled={isLoading}
								className="w-full py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-60 flex items-center justify-center gap-2"
								style={{
									background: 'linear-gradient(45deg, rgb(91, 169, 225), rgb(135, 130, 230))',
								}}
								type="button"
							>
								{isLoading && (
									<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
								)}
								{STRINGS.buttons.getStarted}
							</button>
							<button
								onClick={handleBack}
								disabled={isLoading}
								className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
								type="button"
							>
								{STRINGS.buttons.back}
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
